import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Scroll, X, CheckCircle, Clock } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchConstitution,
  saveConstitutionDraft,
  ratifyConstitution,
  fetchConstitutionVersion,
  type ConstitutionVersionSummary,
} from '@/lib/constitutionApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ConstitutionPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['constitution'], queryFn: fetchConstitution });
  const [draft, setDraft] = useState<string | null>(null);
  const [ratifyOpen, setRatifyOpen] = useState(false);
  const [viewing, setViewing] = useState<ConstitutionVersionSummary | null>(null);

  // Seed the editor once from the server draft.
  if (q.data && draft === null) setDraft(q.data.draftHtml);

  const saveMut = useMutation({
    mutationFn: () => saveConstitutionDraft(draft ?? ''),
    onSuccess: () => { toast.success('Draft saved'); qc.invalidateQueries({ queryKey: ['constitution'] }); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save the draft')),
  });

  const d = q.data;

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Constitution"
        subtitle="Your chambers' governing document. Edit the draft, then ratify it as a dated, immutable version."
      />

      {q.isLoading || !d ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Editor — one card: its status and controls, the field, then the note about the
              field. These were three loose elements on the page background, so the status
              line and the buttons read as page furniture rather than as this editor's own. */}
          <Card className="flex flex-col overflow-hidden lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div className="text-xs font-medium text-muted-foreground">
                {d.currentVersion > 0 ? `Current: version ${d.currentVersion}` : 'Not yet ratified'}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                  {saveMut.isPending ? 'Saving…' : 'Save draft'}
                </Button>
                <Button variant="primary" size="sm" onClick={() => setRatifyOpen(true)}>Ratify new version</Button>
              </div>
            </div>
            {/* Borderless: the card is the field's frame, so a second border inside it would
                draw a box within a box. */}
            <textarea
              value={draft ?? ''}
              onChange={(e) => setDraft(e.target.value)}
              rows={22}
              placeholder="<h1>Constitution of …</h1><p>…</p>"
              className="block w-full resize-y border-0 bg-transparent p-4 font-mono text-xs leading-relaxed text-foreground focus:outline-none focus:ring-0"
            />
            <p className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">HTML supported (headings, lists, tables). The draft is private until you ratify it.</p>
          </Card>

          {/* Current + history */}
          <div className="space-y-4">
            {d.current && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <CheckCircle className="h-4 w-4 text-emerald-500" /> Version {d.current.version} in force
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">Effective {fmtDate(d.current.effectiveFrom)}</div>
                </CardContent>
              </Card>
            )}
            <Card className="overflow-hidden">
              <div className="border-b border-border px-4 py-3 text-xs font-medium text-muted-foreground">Version history</div>
              {d.versions.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">No ratified versions yet.</div>
              ) : (
                <ol className="space-y-2 p-4">
                  {d.versions.map((v) => (
                    <li key={v.id}>
                      <button onClick={() => setViewing(v)} className="w-full rounded-lg border border-border p-3 text-left hover:bg-muted/40">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">Version {v.version}</span>
                          <span className="text-xs text-muted-foreground">{fmtDate(v.effectiveFrom)}</span>
                        </div>
                        {v.summary && <div className="mt-0.5 text-xs text-muted-foreground">{v.summary}</div>}
                        {v.ratifiedBy && <div className="mt-0.5 text-[11px] text-muted-foreground">ratified by {v.ratifiedBy.firstName} {v.ratifiedBy.lastName}</div>}
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>
        </div>
      )}

      {ratifyOpen && (
        <RatifyModal
          nextVersion={(d?.currentVersion ?? 0) + 1}
          onClose={() => setRatifyOpen(false)}
          onDone={() => { setRatifyOpen(false); qc.invalidateQueries({ queryKey: ['constitution'] }); }}
        />
      )}
      {viewing && <VersionModal versionId={viewing.id} onClose={() => setViewing(null)} />}
    </div>
  );
}

function RatifyModal({ nextVersion, onClose, onDone }: { nextVersion: number; onClose: () => void; onDone: () => void }) {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [summary, setSummary] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await ratifyConstitution({
        ...(summary.trim() && { summary: summary.trim() }),
        ...(effectiveFrom && { effectiveFrom: new Date(effectiveFrom).toISOString() }),
      });
      toast.success(`Version ${nextVersion} ratified`);
      onDone();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not ratify'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto w-full max-w-sm rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 text-sm font-semibold text-foreground">Ratify version {nextVersion}</div>
        <div className="space-y-4 px-5 py-4">
          <p className="text-sm text-muted-foreground">This snapshots the current draft as an immutable version. Make sure the draft is final.</p>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Effective date (optional — defaults to today)</span>
            <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Summary of changes (optional)</span>
            <input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="e.g. Adopted at AGM 2026" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          {error && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={saving}>{saving ? 'Ratifying…' : 'Ratify'}</Button>
        </div>
      </div>
    </div>
  );
}

function VersionModal({ versionId, onClose }: { versionId: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ['constitution-version', versionId], queryFn: () => fetchConstitutionVersion(versionId) });
  const v = q.data;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center" onClick={onClose}>
      <ScrollLock />
      <div className="my-8 flex w-full max-w-2xl flex-col rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2"><Scroll className="h-4 w-4 text-brand-600 dark:text-brand-400" /><span className="text-sm font-semibold text-foreground">{v ? `Version ${v.version}` : 'Constitution'}</span></div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
          {!v ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Effective {fmtDate(v.effectiveFrom)}</span>
                {v.ratifiedBy && <span>· ratified by {v.ratifiedBy.firstName} {v.ratifiedBy.lastName}</span>}
              </div>
              <div className="prose prose-sm max-w-none text-sm text-foreground dark:prose-invert" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(v.bodyHtml || '<p>No content.</p>') }} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
